import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { deleteClientNote, listClientNotes } from "../../api/clients";
import { formatShortDate } from "../../utils/formatShortDate";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { useAuth } from "../../context/AuthContext";
import ModalCloseX from "../common/ModalCloseX";

const ClientNotesPanel = forwardRef(function ClientNotesPanel(
  {
    clientId,
    mode = "embedded",
    onClose,
    readOnly = false,
    title = "Client Notes",
    salonTimezone = "",
  },
  ref
) {
  const { currentSalon, salonTimezone: authSalonTimezone } = useAuth();
  const [profileNote, setProfileNote] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const effectiveSalonTimezone =
    salonTimezone || authSalonTimezone || getSalonTimezone(currentSalon);

  const loadNotes = useCallback(async () => {
    if (!clientId) {
      setProfileNote(null);
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await listClientNotes(clientId);
      if (Array.isArray(data)) {
        setProfileNote(null);
        setNotes(data);
      } else {
        setProfileNote(
          data?.profile_note && data.profile_note.note ? data.profile_note : null
        );
        setNotes(Array.isArray(data?.notes) ? data.notes : []);
      }
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to load client notes.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useImperativeHandle(
    ref,
    () => ({
      refresh: loadNotes,
    }),
    [loadNotes]
  );

  const handleDelete = async (noteId) => {
    if (readOnly || !noteId || !clientId) return;
    if (!window.confirm("Delete this note?")) return;
    setDeletingId(noteId);
    setError("");
    try {
      await deleteClientNote(clientId, noteId);
      await loadNotes();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to delete note. Please try again.";
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const containerClass = mode === "modal" ? "px-6 py-4" : "";

  return (
    <div className={`space-y-4 ${containerClass}`.trim()}>
      {mode === "modal" && (
        <div className="flex items-center justify-between bg-rose-500 px-6 py-4 -mx-6 -mt-4">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {onClose && <ModalCloseX onClick={onClose} />}
        </div>
      )}

      {loading && (
        <div className="text-sm text-slate-500">Loading notes...</div>
      )}
      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {!loading && !profileNote && !notes.length && (
        <div className="text-sm text-slate-500">No notes found.</div>
      )}
      {profileNote && (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Profile Notes
          </div>
          <div className="px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap">
            {profileNote.note}
          </div>
        </div>
      )}
      {notes.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <div className="grid grid-cols-4 gap-2 bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <span>Created</span>
            <span>Created By</span>
            <span>Note</span>
            <span></span>
          </div>
          <div className="divide-y divide-slate-200">
            {notes.map((note) => (
              <div
                key={note.id}
                className="grid grid-cols-4 gap-2 px-4 py-3 text-sm text-slate-700"
              >
                <span>{formatShortDate(note.created_at, effectiveSalonTimezone)}</span>
                <span className="flex flex-col leading-tight">
                  <span className="font-semibold text-slate-900">
                    {note.created_by_name || "--"}
                  </span>
                  <span className="text-xs text-slate-500">
                    {note.created_by_id != null ? `ID ${note.created_by_id}` : "--"}
                  </span>
                </span>
                <span className="whitespace-pre-wrap">{note.note}</span>
                <span className="text-right">
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleDelete(note.id)}
                      className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-60"
                      disabled={deletingId === note.id}
                    >
                      {deletingId === note.id ? "..." : "Delete"}
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === "modal" && onClose && (
        <div className="flex justify-end border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-300 px-6 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
});

export default ClientNotesPanel;
